// node_modules
import { FC, useContext, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
// Components
import { TileSidebarMenuItem } from "../../TileSidebarMenuItem";
import { AsynchronousWrapper, CreateSideNote, SideNoteList } from "Components/Shared";
// Types
import { TPassSideNotePost, TSideNoteDTO, TUseFetch } from "Types";
// Enums
import { EntityTypeEnum } from "Enums";
// Logics
import { SideNoteLogicSingleton } from "Logics";
// Contexts
import { CaseContext } from "Stores";
import { DeliveryLayoutContext, SideNotesContext } from "Providers";
// Custom hooks
import { useFetch } from "Hooks";
// style
import tileStyles from "../../tileSidebarMenuItem.module.scss";
import { LoggingConstants } from "Constants";
import { PendoHelperSingleton } from "Helpers";

export const AddCommentTile: FC = () => {
    // Contexts
    const { caseState } = useContext(CaseContext);
    const { sideNotes, setNewSideNotes } = useContext(SideNotesContext);
    const { isRightSidebarCollapsed, setIsOnRightSidebarHoverCollapsed,
        isOnRightSidebarHoverCollapsed, isCommentTileOpen,
        setIsCommentTileOpen } = useContext(DeliveryLayoutContext);

    // Const & Let
    const currentEntityType = caseState.selectedTechnologyId ? EntityTypeEnum.TechnologyType : EntityTypeEnum.CaseType;
    const currentNodeType = SideNoteLogicSingleton.determineSideNoteType(caseState);
    const currentEntityId = caseState.selectedTechnologyId ? caseState.selectedTechnologyId : caseState.selectedCaseId ? caseState.selectedCaseId : -1;
    const sideNoteInfo: TPassSideNotePost = {
        entityId: currentEntityId,
        entityType: currentEntityType,
        isReply: false,
        noteType: currentNodeType
    };
    const sideNoteLocation = currentEntityType === EntityTypeEnum.TechnologyType ? "technology" : "case";
    let extraLocation = "";
    if (currentEntityType === EntityTypeEnum.CaseType) {
        extraLocation = `/${currentNodeType}`;
    }
    // We use `useFetch` custom hook here
    // to fetch data from the server
    const { isLoading, didError, errorMessage, fetchedData }: TUseFetch<TSideNoteDTO[]> = useFetch(`api/sidenotes/${sideNoteLocation}/${currentEntityId}${extraLocation}`);

    // Logging
    useEffect(() => {
        // Log the retrieval of the logs
        PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}SideNotesGet`, {
            sideNoteLocation: sideNoteLocation,
            currentEntityId: currentEntityId,
            extraLocation: extraLocation
        });
    }, [sideNoteLocation, currentEntityId, extraLocation]);

    // Logic
    useEffect(() => {
        // init SideNotesContext data
        setNewSideNotes(fetchedData ? fetchedData : [], currentNodeType);
    }, [currentNodeType, fetchedData, setNewSideNotes]);

    const getText = (): string => {
        let title = "+ Add comment";
        if (SideNoteLogicSingleton.getSideNotesCount(sideNotes) > 0) {
            title = `Comments (${SideNoteLogicSingleton.getSideNotesCount(sideNotes)})`;
        }
        return title;
    };

    const hasNoComments = (): boolean => {
        let hasNoCommentsResult = true;
        if (SideNoteLogicSingleton.getSideNotesCount(sideNotes) > 0) {
            hasNoCommentsResult = false;
        }
        return hasNoCommentsResult;
    };

    const onClickCommentTile = (): void => {
        setIsCommentTileOpen(!isCommentTileOpen);
    };

    return (
        <TileSidebarMenuItem
            id="comments-tile"
            text={getText()}
            onClickHandler={onClickCommentTile}
            imageUrl={"/images/tileSidebarImageComment.png"}
            isOnRightSidebarHoverCollapsed={isOnRightSidebarHoverCollapsed}
            setIsOnRightSidebarHoverCollapsed={setIsOnRightSidebarHoverCollapsed}
            isCollapsedSidebarRight={isRightSidebarCollapsed}
            handleRightSidebarHoveringCollapse={true}>
            {isCommentTileOpen ?
                    <div className={tileStyles.sidebarCommentContainer}>
                        <img className={tileStyles.sidebarCommentCollapsedIcon} src="/images/tileSidebarImageComment.png" alt="Comments" />
                        <div className={tileStyles.sidebarCommentHeaderContainer}>
                            <div className={tileStyles.sidebarCommentCloseContent} title="close comments" onClick={onClickCommentTile}>
                                <FontAwesomeIcon icon={faTimes} />
                            </div>
                            <div className={tileStyles.sidebarCommentContentHeader}>
                                <img src="/images/tileSidebarImageComment.png" alt="Comments" />
                                <div>Comments ({SideNoteLogicSingleton.getSideNotesCount(sideNotes)})</div>
                            </div>
                            <div className={tileStyles.sidebarCommentCreateNewContainer}>
                                <CreateSideNote hasNoComments={hasNoComments()} sideNoteInfo={sideNoteInfo} />
                            </div>
                        </div>
                        <AsynchronousWrapper isLoading={isLoading} didError={didError} errorMessage={errorMessage} LoaderComponent={null}>
                            {sideNotes && sideNotes.length > 0 ?
                                <SideNoteList
                                    sideNotes={sideNotes}
                                    entityType={currentEntityType}
                                    entityId={currentEntityId}
                                    isReplyable={true} />
                                :
                                <></>
                            }
                        </AsynchronousWrapper>
                    </div>
                : 
                    <>
                    </>
            }
        </TileSidebarMenuItem>
    );
};